import { useMemo } from 'react';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';

import { PdfPageCanvas } from '../../PdfCanvas/PdfPageCanvas.tsx';
import { FieldBindingOverlay } from './FieldBindingOverlay.tsx';
import { useFieldBindingCanvas } from './fieldBindingCanvas.hooks.ts';
import type { Props } from './FieldBindingCanvas.types.ts';

// Default render scale when the parent doesn't pass a `scale` prop (mirrors
// PlacementCanvas's default legible zoom level).
const DEFAULT_SCALE = 1.5;

/**
 * The binding surface on its own — one document, field boxes over it.
 *
 * The document and its pages come from `PdfPageCanvas`, which the placement surface uses too:
 * the two used to carry a copy each, so a screen showing both parsed the same PDF twice.
 */
export const FieldBindingCanvas = ({
	file,
	bindings,
	selectedFieldKey,
	scale = DEFAULT_SCALE,
	hidePrefilledFormValues,
	onSelect,
	onBoxChange,
	onAddBox,
	onRemove,
}: Props) => {
	const tMipManage = useTranslationMipManage();
	const {
		pageDims,
		draw,
		bindingsByPage,
		onPageRenderSuccess,
		handleOverlayMouseDown,
		handleOverlayMouseMove,
		finishDraw,
	} = useFieldBindingCanvas({ bindings, scale, onAddBox });

	const errorText = useMemo(
		() => tMipManage('master_documents.field_binding.canvas.load_error'),
		[tMipManage]
	);
	const noDataText = useMemo(
		() => tMipManage('master_documents.field_binding.canvas.no_file'),
		[tMipManage]
	);

	return (
		<PdfPageCanvas
			file={file}
			scale={scale}
			pageDims={pageDims}
			onPageRenderSuccess={onPageRenderSuccess}
			hidePrefilledFormValues={hidePrefilledFormValues}
			testId="field-binding-canvas"
			pageTestId={(pageNum) => `field-binding-canvas-page-${pageNum}`}
			errorText={errorText}
			noDataText={noDataText}
			renderOverlay={({ pageNum, dims }) => (
				<FieldBindingOverlay
					pageNum={pageNum}
					dims={dims}
					scale={scale}
					bindings={bindingsByPage.get(pageNum) ?? []}
					selectedFieldKey={selectedFieldKey}
					draw={draw}
					interactive
					onSelect={onSelect}
					onBoxChange={onBoxChange}
					onRemove={onRemove}
					onMouseDown={handleOverlayMouseDown}
					onMouseMove={handleOverlayMouseMove}
					onFinishDraw={finishDraw}
				/>
			)}
		/>
	);
};
